import { NextRouter } from 'next/router';
import { ParsedUrlQuery } from 'node:querystring';
import { stringify } from 'querystring';

type CatalogWithFiltersQueryParameters = {
  scope: string;
  price: string;
  school: string;
  skill: string;
  difficulty: string;
  duration: string;
  type: string;
  sort: string;
  page: string;
  language: string;
};

export function clearAllFilters(router: NextRouter) {
  const keysToPersist = ['companySlug', 'parameters', 'forcedExperiment'];
  const updatedQuery: ParsedUrlQuery = {};
  for (const [key, value] of Object.entries(router.query)) {
    if (keysToPersist.includes(key)) {
      updatedQuery[key] = value;
    }
  }
  const asPath = getCatalogLink({
    router: {
      ...router,
      query: updatedQuery,
    },
    updatedRouteParameters: {},
  });

  router.replace({ query: updatedQuery }, asPath, {
    shallow: true,
    scroll: true,
  });
}

export function getCatalogLink({
  router,
  updatedRouteParameters,
}: {
  router: NextRouter;
  updatedRouteParameters: Partial<CatalogWithFiltersQueryParameters>;
}) {
  const { parameters, companySlug, ...namedQueryParams } = router.query;
  const baseRoute = Boolean(companySlug) ? `/org/${companySlug}/catalog` : '/catalog';

  const { query: updatedQuery } = getCatalogRouteQuery({
    parameters: { ...namedQueryParams, ...updatedRouteParameters },
  });
  const { parameters: positionalParameters, ...newNamedParams } = updatedQuery;
  const { variant, ...paramsWithoutVariant } = newNamedParams;
  const queryString = stringify(paramsWithoutVariant);
  const path = `${baseRoute}${queryString ? `?${queryString}` : ''}`;

  // Preserve the active Next.js locale prefix so shallow routing does not briefly
  // fall back to the default (English) locale when updating catalog filter params.
  const { locale, defaultLocale } = router;
  if (locale && defaultLocale && locale !== defaultLocale) {
    return `/${locale}${path}`;
  }

  return path;
}

export function updateCatalogUrlState({
  router,
  updatedRouteParameters,
}: {
  router: NextRouter;
  updatedRouteParameters: Partial<CatalogWithFiltersQueryParameters>;
}) {
  const { ...namedQueryParams } = router.query;

  const { query: updatedQuery } = getCatalogRouteQuery({
    parameters: { ...namedQueryParams, ...updatedRouteParameters },
  });
  const asPath = getCatalogLink({
    router: {
      ...router,
      query: updatedQuery,
    },
    updatedRouteParameters: {},
  });

  router.replace({ query: updatedQuery }, asPath, {
    shallow: true,
    scroll: true,
  });

  return;
}

export function getCatalogRouteQuery({ parameters }: { parameters: Partial<CatalogWithFiltersQueryParameters> }) {
  // don't copy over routerQuery values. build them off of parameters
  const updatedQuery: ParsedUrlQuery = {};

  // only the catalog page has a `parameters` array
  // everything else is a named query parameter
  for (const [key, value] of Object.entries(parameters)) {
    if (value !== undefined) {
      updatedQuery[key] = value;
    }
  }

  return {
    query: updatedQuery,
  };
}
